/**
 * The one place times and dates are turned into reader-facing text. Every
 * function takes the BCP 47 locale explicitly (the caller passes
 * `getDisplayLocale()` or `i18n.language`), so the hour cycle and the date
 * order follow the active language: "5:00 PM" in en, "17:00" in de, pl, uk
 * and ja (PD-0004). No store import here — this file stays pure and testable.
 */

/** A wall-clock time ("17:00") in the locale's own hour cycle. */
export const formatClockTime = (hours: number, minutes: number, locale: string): string =>
  new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" })
    .format(new Date(2000, 0, 1, hours, minutes))
    .replace(/ /g, " ");

/** A wire time ("17:00" or "17:00:00") in the locale's own hour cycle. */
export const formatTimeOfDay = (time: string, locale: string): string => {
  const [hours, minutes] = time.split(":").map(Number);
  return formatClockTime(hours, minutes ?? 0, locale);
};

/** A timestamp or calendar date as a medium date ("19. Sept. 2026" in de, "Sep 19, 2026" in en). */
export const formatMediumDate = (value: string | Date, locale: string): string =>
  new Date(value).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });

/** A date with its short weekday, no year ("Thu, Oct 29" in en, "Do., 29. Okt." in de). */
export const formatWeekdayDate = (value: string | Date, locale: string): string =>
  new Date(value).toLocaleDateString(locale, { weekday: "short", month: "short", day: "numeric" });

/** A date range in the locale's own order ("October 14 – 27, 2026" in en, "14.–27. Oktober 2026" in de). */
export const formatDateRange = (start: Date, end: Date, locale: string): string =>
  new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric" })
    .formatRange(start, end)
    .replace(/[\u2009\u202f]/g, " ");

/** Whether the locale reads the clock in 12 hours with AM/PM (en-US, ar) rather than 24 (de, pl, uk, ja). */
export const usesTwelveHourClock = (locale: string): boolean => {
  const hourCycle = new Intl.DateTimeFormat(locale, { hour: "numeric" }).resolvedOptions().hourCycle;
  return hourCycle === "h11" || hourCycle === "h12";
};

/** A length of time in minutes ("1 hour 30 minutes" in en, "1 Stunde, 30 Minuten" in de). */
export const formatDuration = (totalMinutes: number, locale: string): string => {
  const unit = (value: number, name: "hour" | "minute"): string =>
    new Intl.NumberFormat(locale, { style: "unit", unit: name, unitDisplay: "long" }).format(value);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts = [
    ...(hours > 0 ? [unit(hours, "hour")] : []),
    ...(minutes > 0 || hours === 0 ? [unit(minutes, "minute")] : []),
  ];
  return new Intl.ListFormat(locale, { type: "unit", style: "long" }).format(parts);
};

const WEEKDAY_CODES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

/** A wire weekday code ("FRI") as the locale's short weekday name ("Fri" in en, "Fr" in de). */
export const formatWeekdayCode = (code: string, locale: string): string => {
  const index = WEEKDAY_CODES.indexOf(code.toUpperCase());
  if (index < 0) return code;
  // 7 January 2024 was a Sunday; read in UTC so the reader's own zone cannot shift the day.
  return new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(
    new Date(Date.UTC(2024, 0, 7 + index))
  );
};
